import { Component, effect, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { AuthService } from "../../@core/auth/auth.service";
import { ScrollService } from "../../@core/service/scroll.service";
import { ChatStatus, ChatStore } from "../../@core/state/chat.store";
import { SettingsStore } from "../../@core/state/settings.store";
import { ChatInputComponent } from "../chat-input/chat-input.component";
import { LandingDisclaimerComponent } from "../landing/landing-disclaimer/landing-disclaimer.component";
import { ChatAnswerFeedbackComponent } from "./chat-answer/chat-answer-feedback/chat-answer-feedback.component";
import { ChatAnswerComponent } from "./chat-answer/chat-answer.component";
import { ChatQuestionComponent } from "./chat-question/chat-question.component";

@Component({
  selector: 'app-active-chat',
  imports: [
    ChatQuestionComponent,
    ChatAnswerComponent,
    LandingDisclaimerComponent,
    ChatInputComponent,
    ChatAnswerFeedbackComponent,
  ],
  templateUrl: './active-chat.component.html',
  styleUrl: './active-chat.component.scss',
})
export class ActiveChatComponent implements OnDestroy {
  readonly chatService = inject(ChatStore);
  readonly authService = inject(AuthService);
  readonly settings = inject(SettingsStore);
  readonly router = inject(Router);

  readonly scrollService = inject(ScrollService);

  @Input() message?: string;

  _initialized = false;

  readonly isAuthenticated = toSignal(this.authService.isAuthenticated$, {
    initialValue: false,
  });

  stopCallback: () => void;

  constructor() {
    effect(() => {
      if (this.chatService.status() === ChatStatus.STREAMING) {
        this.stopCallback = this.scrollService.scrollBottom('content');
      } else if (this.stopCallback) {
        this.stopScrollService();
      }
    });

    effect(() => {
      if (
        this.isAuthenticated() &&
        this.settings.tosAccepted() &&
        !this._initialized
      ) {
        this._initialized = true;
        this.init();
      }
    });
  }

  private stopScrollService() {
    if (this.stopCallback) {
      this.stopCallback();
      this.stopCallback = undefined;
    }
  }

  ngOnDestroy(): void {
    this.chatService.reset();
    this.stopScrollService();
  }

  init(): void {
    if (this.message) {
      this.chatService.ask(this.message);
      void this.router.navigate([], {
        queryParams: { message: undefined },
        queryParamsHandling: 'merge',
      });
    } else if (!this.chatService.messageCount()) {
      this.chatService.reset();
      void this.router.navigate(['/']);
    }
  }
}
